import type { ReactNode } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { StatusPillSize } from './DeliveryStatusBadge'
import { FieldTooltip } from './FieldLabel'

export interface StatusSelectProps<S extends string> {
  value: string
  /** Left out, the badge is shown on its own, without the menu. */
  onChange?: (state: S) => void
  /** The states offered in the menu; the current one need not be among them. */
  states: S[]
  /** The pill for a state, with `trailing` (the menu caret) inside it. */
  renderBadge: (state: string, trailing?: ReactNode) => ReactNode
  /** What each state means — behind a help icon next to it in the menu. */
  describe: (state: S) => string
  size?: StatusPillSize
}

/**
 * Badge that doubles as a state picker: shows the current state and opens a
 * menu of the states that can be picked instead, each with what it means.
 * The domain-specific pickers (line item, creative) wrap this with their own
 * states, labels and colours.
 */
export function StatusSelect<S extends string>({
  value,
  onChange,
  states,
  renderBadge,
  describe,
  size,
}: StatusSelectProps<S>) {
  const caret = (
    <ChevronDown
      className={`-mr-0.5 text-current opacity-60 ${size === 'md' ? 'size-4' : 'size-3'}`}
    />
  )

  if (!onChange) return <>{renderBadge(value)}</>

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex items-center gap-1 outline-none cursor-pointer">
        {renderBadge(value, caret)}
        {/* With no state there is no pill to hold the caret. */}
        {!value && caret}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={size === 'md' ? 'end' : 'start'} className="min-w-40">
        {states.map((state) => (
          <DropdownMenuItem
            key={state}
            onClick={() => onChange(state)}
            // The help icon is an svg; menu items switch svg hover off.
            className="gap-1.5 [&_svg]:pointer-events-auto"
          >
            {renderBadge(state)}
            <FieldTooltip text={describe(state)} />
            {/* The tooltip only shows on hover, so keyboard users get the meaning read out. */}
            <span className="sr-only">{describe(state)}</span>
            {state === value && <Check className="ml-auto size-3 text-(--ad-text-muted)" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
